import { SharedNestedOptionDropdown } from "@wit/mpesa-ui-components/lib/components/dropdown/shared-dropdown-nested-option-container/shared-dropdown-nested-option-container.component";
import { SharedDropdownOption } from "@wit/mpesa-ui-components/lib/components/dropdown/shared-dropdown-option-container/shared-dropdown-option-container.component";
import i18next from "i18next";
import { isArray, isEqual, isObject, transform } from "lodash";
import moment from "moment";
import { array, object } from "yup";
import { compareVersions } from "../../shared/shared.utils";
import {
  AnnouncementFormSteps,
  AppPlacement,
  AudienceType,
  BusinessAudience,
  ConsumerAudience,
  DeviceType,
  DurationType,
  GsmProfile,
  IAnnouncement,
  IIcon,
  IStoryInterface,
  IVersion,
} from "./announcements.model";
import { colorDefaultToDescription, colorDefaultToTitle } from "./pages/announcement-storys.page";
import { INotification } from "../safaricom-push-notifications/push-notifications.modal";
import { IConfig } from "../../app.component";

export const AnnouncementsUtils = {
  capitalize: (s: String) => {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  },
  sortArrayOfVersions: (array: string[]) => {
    return array.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  },

  getDurationbyEndDate: (startDate: string, endDate: string): DurationType | undefined => {
    const byDays = moment(endDate as string).diff(moment(startDate as string), "days");
    const byWeeks = moment(endDate as string).diff(moment(startDate as string), "weeks");
    const byMonths = moment(endDate as string).diff(moment(startDate as string), "months");
    if (byDays <= 2) {
      if (byDays === 1) {
        return DurationType.ONE_DAY;
      }
      if (byDays === 2) {
        return DurationType.TWO_DAY;
      }
    }
    if (byWeeks <= 3) {
      if (byWeeks <= 1) {
        return DurationType.ONE_WEEK;
      }
      if (byWeeks === 2) {
        return DurationType.TWO_WEEK;
      }
      if (byWeeks === 3) {
        return DurationType.THREE_WEEK;
      }
    }
    if (byMonths <= 2) {
      if (byMonths === 1) {
        return DurationType.ONE_MONTH;
      }
      if (byMonths === 2) {
        return DurationType.TWO_MONTH;
      }
    }
    return byMonths > 2 ? DurationType.TWO_MONTH : undefined;
  },
  getEndDate: (startDate: string, duration: DurationType | null): string => {
    if (!duration) {
      return moment(startDate)
        .format("YYYY-MM-DD[T]HH:mm:ssZ")
        .toString();
    }

    let amount: number = 0;
    if (duration === DurationType.ONE_DAY || duration === DurationType.TWO_DAY) {
      if (duration === DurationType.ONE_DAY) {
        amount = 1;
      } else {
        amount = 2;
      }
      return moment(startDate)
        .add(amount, "days")
        .format("YYYY-MM-DD[T]HH:mm:ssZ")
        .toString();
    }

    if (
      duration === DurationType.ONE_WEEK ||
      duration === DurationType.TWO_WEEK ||
      duration === DurationType.THREE_WEEK
    ) {
      if (duration === DurationType.ONE_WEEK) {
        amount = 1;
      } else if (duration === DurationType.TWO_WEEK) {
        amount = 2;
      } else {
        amount = 3;
      }
      return moment(startDate)
        .add(amount, "weeks")
        .format("YYYY-MM-DD[T]HH:mm:ssZ")
        .toString();
    }

    if (duration === DurationType.ONE_MONTH || duration === DurationType.TWO_MONTH) {
      if (duration === DurationType.ONE_MONTH) {
        amount = 1;
      } else {
        amount = 2;
      }
      return moment(startDate)
        .add(amount, "months")
        .format("YYYY-MM-DD[T]HH:mm:ssZ")
        .toString();
    }
    return moment()
      .format("YYYY-MM-DD[T]HH:mm:ssZ")
      .toString();
  },
  getIconSelected: (iconId: string | undefined): IIcon => {
    const iconDefault = {
      id: "000",
      label: i18next.t("pages.announcements.detailPage.rows.gallery"),
      category: "ACTION",
      base64:
        "data:image/jpeg;base64,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",
    };
    if (iconId === undefined) {
      return iconDefault;
    }
    const iconsStorage: IIcon[] = JSON.parse(sessionStorage.getItem("icons") as string);
    const iconById = iconsStorage ? iconsStorage.find(icon => icon.id === iconId) : undefined;
    return iconById !== undefined ? iconById : iconDefault;
  },
  getStorysToBeDeleted: (announcement: IAnnouncement, editedValues: IAnnouncement): string[] => {
    const storysToRemove: string[] = [];
    announcement.stories.forEach(value => {
      if (!editedValues.stories.find(s => s.id === value.id)) {
        storysToRemove.push(value.id);
      }
    });
    return storysToRemove;
  },
  getStorysToBeAdded: (announcement: IAnnouncement, editedValues: IAnnouncement): IStoryInterface[] => {
    const storysToAdd: IStoryInterface[] = [];
    editedValues.stories.forEach(value => {
      if (!announcement.stories.find(s => s.id === value.id)) {
        storysToAdd.push(value);
      }
    });
    return storysToAdd;
  },
  diference: (original: object, newObj: object): IAnnouncement => {
    const diff: IAnnouncement = AnnouncementsUtils.changes(newObj, original) as IAnnouncement;
    if (diff.audience !== undefined) {
      if (AnnouncementsUtils.isBusinessAudience(diff.audience)) {
        diff.targetConsumer = [];
      }
      if (AnnouncementsUtils.isConsumerAudience(diff.audience)) {
        diff.targetBusinessMerchant = [];
        diff.targetBusinessAgent = [];
      }
    }
    if (diff.stories !== undefined) {
      const storysToEdit: IStoryInterface[] = [];
      diff.stories.forEach(story => {
        if (story.id !== undefined) {
          const storyToAddType: IStoryInterface[] = (newObj as IAnnouncement).stories.filter(s => s.id === story.id);
          if (storyToAddType.length === 1) {
            if (story.callToAction) {
              story.callToAction = storyToAddType[0].callToAction;
              story.callAction = storyToAddType[0].callAction;
            } else if (storyToAddType[0].callAction) {
              story.callAction = storyToAddType[0].callAction;
            }
          }
          if (story.title !== null || story.title !== undefined) {
            story.colorTitle = storyToAddType[0].colorTitle ? storyToAddType[0].colorTitle : colorDefaultToTitle;
          }
          if (story.description !== null || story.description !== undefined) {
            story.colorDescription = storyToAddType[0].colorDescription
              ? storyToAddType[0].colorDescription
              : colorDefaultToDescription;
          }
          storysToEdit.push(story);
        }
      });
      diff.stories = storysToEdit;
    }

    if (diff.pushNotificationDetails !== undefined) {
      let pushNotificationDetails = diff.pushNotificationDetails;
      if (!pushNotificationDetails?.title) {
        pushNotificationDetails = {
          ...pushNotificationDetails,
          title: (original as IAnnouncement).pushNotificationDetails?.title,
        };
      }
      if (!pushNotificationDetails?.description) {
        pushNotificationDetails = {
          ...pushNotificationDetails,
          description: (original as IAnnouncement).pushNotificationDetails?.description,
        };
      }
      diff.pushNotificationDetails = pushNotificationDetails;
    }
    return diff;
  },
  isBusinessAudience: (audience: AudienceType): boolean => {
    return Object.keys(BusinessAudience).includes(audience);
  },
  isConsumerAudience: (audience: AudienceType): boolean => {
    return Object.keys(ConsumerAudience).includes(audience);
  },
  changes: (newObj: any, origObj: any): object => {
    let arrayIndexCounter = 0;
    return transform(newObj, function(result: any, value, key) {
      if (((key as unknown) as string) === "id") {
        result[key] = value;
      }
      if (!isEqual(value, origObj[key])) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key;
        result[resultKey] =
          isObject(value) && isObject(origObj[key]) ? AnnouncementsUtils.changes(value, origObj[key]) : value;
      }
    });
  },
  timeOptions: (): SharedDropdownOption[] => {
    const options: SharedDropdownOption[] = [];

    for (let i = 0; i <= 11; i++) {
      options.push({ label: `${i}:00 am`, key: `${i}:00` });

      options.push({ label: `${i}:30 am`, key: `${i}:30` });
    }

    options.push({ label: `12:00 pm`, key: `12:00` });
    options.push({ label: `12:30 pm`, key: `12:30` });

    for (let i = 1; i <= 11; i++) {
      options.push({ label: `${i}:00 pm`, key: `${i}:00` });

      options.push({ label: `${i}:30 pm`, key: `${i}:30` });
    }

    return options;
  },
  addTimeToDate: (dateToAdd: string, timeToAdd: string): [string, boolean] => {
    const period = timeToAdd.slice(-2);
    const time = timeToAdd.slice(0, timeToAdd.length - 3).split(":");
    if (time.length > 0) {
      let hour: number = (time[0] as unknown) as number;
      const minute: number = (time[1] as unknown) as number;
      if (period === "pm" && hour < 12) {
        hour = parseInt(hour.toString()) + parseInt("12");
      }
      if (
        moment().valueOf() <
        moment(dateToAdd)
          .set("hours", hour)
          .set("minutes", minute)
          .set("seconds", 0)
          .valueOf()
      ) {
        return [
          moment(dateToAdd)
            .set("hours", hour)
            .set("minutes", minute)
            .set("seconds", 0)
            .format("YYYY-MM-DD[T]HH:mm:ssZ"),
          true,
        ];
      }
    }
    return [moment(dateToAdd).format("YYYY-MM-DD[T]HH:mm:ssZ"), false];
  },
  cleanMsisdn: (validTargets: string[]): string[] => {
    const valid: string[] = [];
    validTargets.forEach(v => {
      if (!valid.includes(v.slice(-9))) {
        valid.push(v.slice(-9));
      }
    });
    return valid;
  },
  getClearMsisdn: (validTargets: string[]): string[] => {
    return validTargets.map(v => {
      return v.slice(-9);
    });
  },
  getSizeAboutTargets: (targets: string[] | undefined | null): number => {
    if (!targets) {
      return 0;
    }
    return targets.reduce((previous, current) => previous + current.length, 0);
  },

  getGsmProfileOptions: () => {
    const options: SharedDropdownOption[] = [];
    Object.values(GsmProfile).forEach(profile => {
      options.push({
        label: i18next.t(`pages.announcements.gsmProfileEnum.${profile}`),
        key: profile,
      });
    });
    return options;
  },
  getAppPlacementOptions: () => {
    const options: SharedDropdownOption[] = [];
    Object.values(AppPlacement).forEach(appPlacement => {
      options.push({
        label: i18next.t(`pages.announcements.appPlacementEnum.${appPlacement}`),
        key: appPlacement,
      });
    });
    return options;
  },
  getAudienceOptions: (
    audience: AudienceType[] | undefined,
    businessAgentEnabled: boolean,
    consumerChildEnabled: boolean,
    isBusinessDisabled?: boolean,
  ) => {
    const options: SharedNestedOptionDropdown[] = [];

    options.push({
      label: i18next.t(`pages.announcements.audienceEnum.${BusinessAudience.ALL_BUSINESSAPPS}`),
      key: BusinessAudience.ALL_BUSINESSAPPS,
      disabled: !!isBusinessDisabled,
      children:
        audience &&
        Object.keys(BusinessAudience).some(
          item => audience.includes(item as AudienceType) && item !== BusinessAudience.BUSINESSAPP_UNIFIED,
        )
          ? [
              {
                label: i18next.t(`pages.announcements.audienceEnum.${BusinessAudience.BUSINESSAPP_AGENT}`),
                key: BusinessAudience.BUSINESSAPP_AGENT,
                disabled: !businessAgentEnabled,
              },
              {
                label: i18next.t(`pages.announcements.audienceEnum.${BusinessAudience.BUSINESSAPP_MERCHANT}`),
                key: BusinessAudience.BUSINESSAPP_MERCHANT,
              },
            ]
          : [],
      hideArrow: true,
      openOptions: true,
    });
    options.push({
      label: i18next.t(`pages.announcements.audienceEnum.${BusinessAudience.BUSINESSAPP_UNIFIED}`),
      key: BusinessAudience.BUSINESSAPP_UNIFIED,
      children: [],
      hideArrow: true,
      openOptions: false,
    });
    options.push({
      label: i18next.t(`pages.announcements.audienceEnum.${ConsumerAudience.ALL_CONSUMERAPPS}`),
      key: consumerChildEnabled ? ConsumerAudience.ALL_CONSUMERAPPS : ConsumerAudience.CONSUMERAPP,
      children:
        audience &&
        Object.keys(ConsumerAudience).some(item => audience.includes(item as AudienceType)) &&
        consumerChildEnabled
          ? [
              {
                label: i18next.t(`pages.announcements.audienceEnum.${ConsumerAudience.CONSUMERAPP_ADULT}`),
                key: ConsumerAudience.CONSUMERAPP_ADULT,
              },
              {
                label: i18next.t(`pages.announcements.audienceEnum.${ConsumerAudience.CONSUMERAPP_CHILD}`),
                key: ConsumerAudience.CONSUMERAPP_CHILD,
                disabled: !consumerChildEnabled,
              },
            ]
          : [],
      hideArrow: true,
      openOptions: consumerChildEnabled,
    });

    options.push({
      label: i18next.t(`pages.announcements.audienceEnum.${ConsumerAudience.CONSUMERAPP_OTT}`),
      key: ConsumerAudience.CONSUMERAPP_OTT,
      children: [],
      hideArrow: true,
      openOptions: false,
    });
    return options;
  },
  removeDuplicatesFromArray: (array: any[]) => {
    return Array.from(new Set(array));
  },
  getSelectedVersions: (values: IAnnouncement | INotification, deviceType: DeviceType) => {
    let selectedVersions = [] as string[];
    if (values.targetVersions) {
      values.targetVersions.forEach(targetVersion => {
        if (targetVersion.deviceType === deviceType) {
          selectedVersions = [...selectedVersions, ...targetVersion.appBuildVersions];
        }
      });
    }
    return selectedVersions;
  },
  getOptionsChildren: (opt: SharedNestedOptionDropdown) => {
    let options = [] as string[];
    if (opt.children) {
      opt.children.forEach((child: SharedNestedOptionDropdown) => {
        options = [...options, ...AnnouncementsUtils.getOptionsChildren(child), child.key];
      });
    }
    return options;
  },
  getTargetVersionsObject: (newSelectedVersions: string[], allAppVersions: IVersion[], deviceType: DeviceType) => {
    const targetVersions = [] as IVersion[];
    if (newSelectedVersions.includes("all-versions-key")) {
      allAppVersions.forEach(appVersion => {
        targetVersions.push({ ...appVersion, deviceType });
      });
    } else {
      newSelectedVersions.forEach(version => {
        allAppVersions.forEach(appVersion => {
          if (appVersion.appBuildVersions.includes(version)) {
            const targetVersion = targetVersions.find(
              targetVersion =>
                targetVersion.appVersion === appVersion.appVersion && targetVersion.deviceType === deviceType,
            );
            if (targetVersion) {
              targetVersion.appBuildVersions.push(version);
            } else {
              const newAppVersion = { ...appVersion, appBuildVersions: [version], deviceType };
              targetVersions.push(newAppVersion);
            }
          }
        });
      });
    }
    return targetVersions;
  },
  validateStepAppVersion: () => {
    return object().shape({
      targetVersions: array()
        .min(1)
        .required(i18next.t("commons.mandatoryField")),
    });
  },
  getFormIdsByStep: (step: number) => {
    switch (step) {
      case 1:
        return AnnouncementFormSteps.STEP_ONE;
      case 2:
        return AnnouncementFormSteps.STEP_TWO;
      case 3:
        return AnnouncementFormSteps.STEP_THREE;
      case 4:
        return AnnouncementFormSteps.STEP_FOUR;
      default:
        break;
    }
  },

  areDeprecatedVersionsSelected: (values: IAnnouncement, minVersion: string) => {
    if (values.targetVersions) {
      return values.targetVersions.some(version => compareVersions(version.appVersion, minVersion) === -1);
    }
    return false;
  },

  isGsmProfileSelected: (gsmProfile: GsmProfile[] | undefined, opt: any) => {
    return gsmProfile?.includes(opt);
  },

  isAppPlacementSelected: (appPlacement: AppPlacement | undefined, opt: any) => {
    return appPlacement === opt;
  },

  isOptionSelected: (
    audience: AudienceType[] | undefined,
    opt: any,
    businessAgentEnabled = true,
    consumerChildEnabled = true,
  ) => {
    if (
      audience &&
      audience.includes(BusinessAudience.ALL_BUSINESSAPPS) &&
      (opt === BusinessAudience.ALL_BUSINESSAPPS ||
        (opt === BusinessAudience.BUSINESSAPP_AGENT && businessAgentEnabled) ||
        opt === BusinessAudience.BUSINESSAPP_MERCHANT)
    ) {
      return true;
    } else if (
      audience &&
      audience.includes(ConsumerAudience.ALL_CONSUMERAPPS) &&
      (opt === ConsumerAudience.ALL_CONSUMERAPPS ||
        (opt === ConsumerAudience.CONSUMERAPP_CHILD && consumerChildEnabled) ||
        opt === ConsumerAudience.CONSUMERAPP_ADULT)
    ) {
      return true;
    }
    return audience?.includes(opt);
  },
  isSafaricomTab: (profileTypesEnabled: boolean, appPlacement: AppPlacement | undefined) => {
    return profileTypesEnabled && appPlacement === AppPlacement.SAFARICOM_TAB;
  },
  getImageConstraintValues: (profileTypesEnabled: boolean, isNewImage: boolean, appPlacement?: AppPlacement) => {
    const constImageValues = {
      MIN_WIDTH: 160,
      MIN_HEIGHT: 120,
      MAX_FILE_SIZE: 256000,
      VALID_RATIO: 4 / 3, // width : height
      RATIO_STRING: "4:3",
    };

    const constImageValuesCarousel = {
      MIN_WIDTH: 320,
      MIN_HEIGHT: 160,
      MAX_FILE_SIZE: 300000,
      VALID_RATIO: 2 / 1, // width : height
      RATIO_STRING: "2:1",
    };
    if (isNewImage) {
      return constImageValuesCarousel;
    } else if (profileTypesEnabled) {
      if (appPlacement === AppPlacement.SAFARICOM_TAB) {
        constImageValues.MIN_HEIGHT = 96;
        return constImageValues;
      } else if (appPlacement === AppPlacement.HOMESCREEN) {
        constImageValues.MIN_HEIGHT = 156;
        constImageValues.MIN_WIDTH = 104;
        constImageValues.VALID_RATIO = 2 / 3;
        constImageValues.RATIO_STRING = "2:3";

        return constImageValues;
      }
    }

    return constImageValues;
  },
  getFieldsValidation: (appPlacement?: AppPlacement) => {
    const fielsValidation = {
      TITLE_MAX_LENGTH: 15,
      DESCRITPION_MAX_LENGTH: 48,
    };

    if (appPlacement && appPlacement === AppPlacement.HOMESCREEN) {
      fielsValidation.DESCRITPION_MAX_LENGTH = 25;
    }

    return fielsValidation;
  },
  validateIsNewBusinessCard: (config?: IConfig, versions?: IVersion[]): boolean => {
    return (
      !!config &&
      !!config.appVersionDependencies.businessAnnouncementsThumbnail &&
      !!versions &&
      versions?.length > 0 &&
      Object.values(versions).some(
        version =>
          compareVersions(
            version.appVersion,
            (config.appVersionDependencies.businessAnnouncementsThumbnail as { min: string; max: string }).min,
          ) >= 0,
      )
    );
  },
};
